import { ref, computed, readonly, onMounted, watch } from 'vue'
import { useKotlinBridge } from './useKotlinBridge'
import type MartinBridge from '@/types/martin-bridge'

// Singleton auth state
const authState = ref<MartinBridge.JsAuthState>({
  isAuthenticated: false,
  isLoading: false,
  user: null,
  error: null
})

let authBridge: MartinBridge.AuthBridge | null = null

/**
 * Composable for authentication state and actions.
 */
export function useAuth() {
  const { bridge, isLoaded } = useKotlinBridge()

  const isAuthenticated = computed(() => authState.value.isAuthenticated)
  const isLoading = computed(() => authState.value.isLoading)
  const user = computed(() => authState.value.user)
  const error = computed(() => authState.value.error)
  const isAdmin = computed(() => authState.value.user?.role === 'ADMIN')

  const initAuthBridge = () => {
    if (authBridge) return authBridge

    if (bridge.value) {
      authBridge = bridge.value.createAuthBridge()
      authBridge.stateObserver.subscribe((state: MartinBridge.JsAuthState) => {
        authState.value = { ...state }
      })
      return authBridge
    }
    return null
  }

  const checkAuth = () => {
    const bridgeInst = initAuthBridge()
    bridgeInst?.refreshAuth()
  }

  const loginWithGitHub = () => {
    // Use backend callback endpoint that's registered in GitHub OAuth App
    // The backend will redirect back to frontend /auth/callback after processing
    const redirectUri = encodeURIComponent(`${window.location.origin}/api/auth/github/callback/web`)
    window.location.href = `/api/auth/github?redirectUri=${redirectUri}`
  }

  const handleAuthCallback = (code: string, redirectUri?: string) => {
    const bridgeInst = initAuthBridge()
    bridgeInst?.handleAuthCallback(code, redirectUri)
  }

  const logout = () => {
    authBridge?.logout()
    // Reset state
    authState.value = {
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null
    }
  }

  const loginWithToken = async (token: string) => {
    localStorage.setItem('martin_auth_token', token)
    try {
      const res = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        localStorage.setItem('martin_user', JSON.stringify(data))
        authState.value = {
          isAuthenticated: true,
          isLoading: false,
          user: {
            id: data.id,
            name: data.name,
            email: data.email,
            displayName: data.displayName,
            avatarUrl: data.avatarUrl,
            githubUsername: data.githubUsername,
            role: data.role
          },
          error: null
        }
      }
    } catch (e) {
      console.error('[useAuth] loginWithToken failed:', e)
    }
  }

  const clearError = () => {
    authBridge?.clearError()
  }

  // Auto-init when bridge is available
  onMounted(() => {
    if (isLoaded.value) {
      initAuthBridge()
    }
  })

  // Watch for bridge loading
  watch(isLoaded, (loaded) => {
    if (loaded && !authBridge) {
      initAuthBridge()
    }
  })

  return {
    // State
    isAuthenticated: readonly(isAuthenticated),
    isLoading: readonly(isLoading),
    user: readonly(user),
    error: readonly(error),
    authState: readonly(authState),
    isAdmin: readonly(isAdmin),

    // Actions
    checkAuth,
    loginWithGitHub,
    handleAuthCallback,
    logout,
    clearError,
    loginWithToken
  }
}
